import React, { useState, useEffect, Fragment } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';
import {
    EuiFlexGroup,
    EuiText,
    EuiForm,
    EuiFlexItem,
    EuiFormRow,
    EuiButton,
    EuiRadioGroup,
    EuiCheckbox,
    EuiToolTip,
} from '@elastic/eui';
import { useForm, Controller } from 'react-hook-form';
import { addOutletIntoRestaurant, editOutletIntoRestaurant } from '../../../api/restaurantAPICall';
import { isEmpty } from 'lodash';
import TextField from '../../../components/Form/TextField';
import MobileNumber from '../../../components/Form/MobileNumber';
import TextAreaField from '../../../components/Form/TextAreaField';
import SelectField from '../../../components/Form/SelectField';
import NumberField from '../../../components/Form/NumberField';
import RadioField from '../../../components/Form/RadioField';
import KitchenTimeSlot from '../../../components/Form/KitchenTimeSlot';
import CheckBoxField from '../../../components/Form/CheckBoxField';
import ComboBoxField from '../../../components/Form/ComboBoxField';
import TimeSlot from '../../../components/Form/TimeSlot';
import { currencyData } from '../../../components/settings/countrySymbolAndInforamtion';
import { allCountry } from '../../../components/settings/allCountry';
import APIV1 from '../../../api/axios/APIV1';
import API from '../../../api/axios/API';
import { SET_EDIT_OUTLET } from '../../../reduxStore/types/outlet';

const CreateOutletScreen = ({ value, subscriptionId }) => {
    const location = useLocation();
    const history = useHistory();
    const availableLanguages = useSelector((state) => state.language.availableLanguages);
    const restaurantId = useSelector((state) => state.auth.selectedRestaurantId);
    const editOutlet = useSelector((state) => state.outlet.editOutlet);
    const [loader, setLoader] = useState(false);
    const [outletData, setOutletData] = useState(null);
    console.log(`outletData ==>`, outletData);
    const [subscriptionConfigurations, setSubscriptionConfigurations] = useState([]);
    const dispatch = useDispatch();
    const defaultValue = {};
    const [timeSlotError, setTimeSlotError] = useState(false);
    const {
        handleSubmit,
        formState: { errors },
        control,
        watch,
        reset,
        setValue,
        getValues,
    } = useForm({
        defaultValues: { ...defaultValue },
        mode: 'onBlur', // This will show errors when user leaves the field
        reValidateMode: 'onChange', // This will revalidate on every change after initial error
    });
    useEffect(() => {
        const fetchSubscriptionConfigurations = async () => {
            if (!restaurantId) return;

            try {
                const response = await API.get(`/restaurants/${restaurantId}/subscription-configurations`);
                // The interceptor returns response.data, so 'response' here is already the data
                if (response?.success && response?.subscription_configurations) {
                    const formattedConfigs = response.subscription_configurations.map((config) => ({
                        label: `${config.duration} days - ₹${config.amount}`,
                        value: config.id,
                    }));
                    setSubscriptionConfigurations(formattedConfigs);
                }
            } catch (error) {
                console.error('Error fetching subscription configurations:', error);
            }
        };

        fetchSubscriptionConfigurations();
    }, [restaurantId]);

    useEffect(() => {
        return () => {
            // Reset outlet data when leaving the page
            setOutletData(null);
        };
    }, []);

    // Fetch outlet data when outletId is present
    useEffect(() => {
        if (subscriptionId) {
            fetchOutletData();
        }
    }, [subscriptionId]);

    // Add a useEffect to preserve time slot values when form changes
    useEffect(() => {
        // Only run this effect if we have existing values and not during initial load
        if (!editOutlet || !outletData) return;

        const currentValues = getValues();

        // Preserve time slot values if they exist
        const preservedValues = {};

        // Store preserved values to apply after reset
    }, []);

    const fetchOutletData = async () => {
        try {
            setLoader(true);
            const response = await APIV1.get(
                `/subscription-requests/${subscriptionId}?restaurant_id=${restaurantId}&subscription_request_id=${subscriptionId}`
            );
            if (response && response.subscription_request) {
                const subscriptionRequest = response.subscription_request;
                const firstParse = subscriptionRequest.outlet_data;
                const parsedOutletData = JSON.parse(firstParse);
                parsedOutletData.plan_id = subscriptionRequest.plan_id;
                setOutletData(parsedOutletData);
            }
        } catch (error) {
            console.error('Error fetching outlet data:', error);
        } finally {
            setLoader(false);
        }
    };
    useEffect(() => {
        if (editOutlet && outletData) {
            let translationObject = {
                name: {},
                city: {},
                state: {},
                address_line_1: {},
                address_line_2: {},
            };

            for (const languageId in outletData.translations.name) {
                translationObject.name[languageId] = outletData.translations.name[languageId];
            }

            for (const languageId in outletData.address.translations.city) {
                translationObject.city[languageId] = outletData.address.translations.city[languageId];
            }
            for (const languageId in outletData.address.translations.state) {
                translationObject.state[languageId] = outletData.address.translations.state[languageId];
            }
            for (const languageId in outletData.address.translations.address_line_1) {
                translationObject.address_line_1[languageId] =
                    outletData.address.translations.address_line_1[languageId];
            }
            for (const languageId in outletData.address.translations.address_line_2) {
                translationObject.address_line_2[languageId] =
                    outletData.address.translations.address_line_2[languageId];
            }

            defaultValue.translations = translationObject;
            defaultValue.fssai_licence = outletData?.fssai_licence || '';
            // defaultValue.contact_number = outletData.outlet_address.contact_number;
            defaultValue.country_code = outletData?.address?.country_code ? outletData.address.country_code : '+91';
            defaultValue.contact_number = outletData.address.contact_number;
            // Fix iso_country_code - if it's a phone prefix like "+91", default to "IN"
            if (outletData?.iso_country_code && !outletData.iso_country_code.startsWith('+')) {
                defaultValue.iso_country_code = outletData.iso_country_code;
            } else if (outletData?.address?.iso_country_code && !outletData.address.iso_country_code.startsWith('+')) {
                defaultValue.iso_country_code = outletData.address.iso_country_code;
            } else {
                // Default to IN if iso_country_code is missing or is a phone prefix
                defaultValue.iso_country_code = 'IN';
            }
            console.log('ISO country code set to:', defaultValue.iso_country_code);

            defaultValue.city = outletData.address.city;
            defaultValue.state = outletData.address.state;
            // defaultValue.country_code = outletData.address.country;
            defaultValue.contact_number = outletData.address.contact_number
                ? `${outletData.address.country_code}${outletData.address.contact_number}`
                : outletData.address.contact_number;
            defaultValue.latitude = outletData.address.latitude;
            defaultValue.longitude = outletData.address.longitude;
            defaultValue.service_radius = outletData.address.service_radius;
            defaultValue.gst_applicable = outletData.settings.gst_applicable;
            defaultValue.gst_number = outletData.settings.gst_applicable ? outletData.settings.gst_number : null;
            defaultValue.delivery_charges_gst_slab = outletData.settings.gst_applicable
                ? outletData.settings.delivery_charges_gst_slab.toString()
                : null;
            defaultValue.kitchen_preparation_time = outletData.settings.kitchen_preparation_time;
            defaultValue.delivery_charge_threshold = outletData.settings.delivery_charge_threshold;
            defaultValue.delivery_charge_above_threshold = outletData.settings.delivery_charge_above_threshold;
            defaultValue.delivery_charge_below_threshold = outletData.settings.delivery_charge_below_threshold;
            defaultValue.packaging_charge = outletData.settings.packaging_charge;
            // Handle both spellings of convenience charge (API might have typo)
            defaultValue.convenience_charge = outletData.settings?.convenience_charge;
            console.log('Convenience charge set to:', defaultValue.convenience_charge);
            defaultValue.kitchen_preparation_time = outletData.settings.kitchen_preparation_time;
            defaultValue.vegetarian_flag = outletData.settings?.vegetarian_flag ? true : false;
            defaultValue.non_vegetarian_flag = outletData.settings?.not_vegetarian_flag ? true : false;
            defaultValue.egg_flag = outletData.settings?.egg_flag ? true : false;
            defaultValue.extra_delivery_charge = outletData.settings.extra_delivery_charge;
            defaultValue.normal_delivery_radius = outletData.address.normal_delivery_radius;
            defaultValue.prize_per_kilometer = defaultValue.normal_delivery_radius ? true : false;
            defaultValue.currency_symbol = [
                {
                    value: outletData.settings.currency_symbol,
                    label: outletData.settings.currency_symbol,
                },
            ];
            defaultValue.currency_code = [
                {
                    value: outletData.settings.currency_code,
                    label: outletData.settings.currency_code,
                },
            ];
            defaultValue.delivery = outletData.settings?.delivery ? true : false;
            defaultValue.dine_in = outletData.settings?.dine_in ? true : false;
            defaultValue.takeaway = outletData.settings?.takeaway ? true : false;
            defaultValue.case_on_delivery = outletData.case_on_delivery ? true : false;

            // Set kitchen rush hour slot
            console.log('Rush hour time slot setting:', outletData.settings?.rush_hour_time_slot_setting);
            defaultValue.kitchen_time_slot =
                outletData.settings?.rush_hour_time_slot_setting === 'no_time_slots'
                    ? 6
                    : outletData.settings?.rush_hour_time_slot_setting === 'same_time_slot_for_all_days'
                      ? 7
                      : outletData.settings?.rush_hour_time_slot_setting === 'different_time_slots_for_different_days'
                        ? 8
                        : 6;
            console.log('Set kitchen_time_slot to:', defaultValue.kitchen_time_slot);

            if (outletData.settings?.rush_hour_time_slot_setting === 'same_time_slot_for_all_days') {
                console.log('Rush hour schedules:', outletData.settings.rush_hour_schedules);
                let fieldData = [];
                if (outletData.settings.rush_hour_schedules && outletData.settings.rush_hour_schedules.length > 0) {
                    // For same time slot for all days, we expect one entry with all days
                    const rushHourData = outletData.settings.rush_hour_schedules[0];
                    if (rushHourData && rushHourData.time_slots) {
                        rushHourData.time_slots.forEach((item) => {
                            fieldData.push({
                                id: uuidv4(),
                                start_time: item.start_time,
                                end_time: item.end_time,
                                preparation_time_in_rush: item.preparation_time_in_rush,
                            });
                        });
                    }
                }
                console.log('Kitchen time fieldData:', fieldData);
                defaultValue.kitchen_time = fieldData;
            }

            defaultValue.kitchen_time_weak = {
                sunday: [],
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
            };

            if (outletData.settings?.rush_hour_time_slot_setting === 'different_time_slots_for_different_days') {
                console.log('Processing different rush hour days, raw data:', outletData.settings.rush_hour_schedules);

                // Process the rush hour schedules directly based on the actual data structure
                if (outletData.settings.rush_hour_schedules && outletData.settings.rush_hour_schedules.length > 0) {
                    outletData.settings.rush_hour_schedules.forEach((schedule) => {
                        const days = schedule.days_of_week || [];
                        const timeSlots = schedule.time_slots || [];

                        console.log('Processing rush hour schedule - days:', days, 'timeSlots:', timeSlots);

                        days.forEach((day) => {
                            // Map day numbers to week days
                            if (day === 1) {
                                timeSlots.forEach((item) =>
                                    defaultValue.kitchen_time_weak.monday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                        preparation_time_in_rush: item.preparation_time_in_rush || '',
                                    })
                                );
                            }
                            if (day === 2) {
                                timeSlots.forEach((item) =>
                                    defaultValue.kitchen_time_weak.tuesday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                        preparation_time_in_rush: item.preparation_time_in_rush || '',
                                    })
                                );
                            }
                            if (day === 3) {
                                timeSlots.forEach((item) =>
                                    defaultValue.kitchen_time_weak.wednesday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                        preparation_time_in_rush: item.preparation_time_in_rush || '',
                                    })
                                );
                            }
                            if (day === 4) {
                                timeSlots.forEach((item) =>
                                    defaultValue.kitchen_time_weak.thursday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                        preparation_time_in_rush: item.preparation_time_in_rush || '',
                                    })
                                );
                            }
                            if (day === 5) {
                                timeSlots.forEach((item) =>
                                    defaultValue.kitchen_time_weak.friday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                        preparation_time_in_rush: item.preparation_time_in_rush || '',
                                    })
                                );
                            }
                            if (day === 6) {
                                timeSlots.forEach((item) =>
                                    defaultValue.kitchen_time_weak.saturday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                        preparation_time_in_rush: item.preparation_time_in_rush || '',
                                    })
                                );
                            }
                            if (day === 7) {
                                timeSlots.forEach((item) =>
                                    defaultValue.kitchen_time_weak.sunday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                        preparation_time_in_rush: item.preparation_time_in_rush || '',
                                    })
                                );
                            }
                        });
                    });
                }

                console.log('Processed kitchen_time_weak:', defaultValue.kitchen_time_weak);
            }

            // Set outlet time slot
            defaultValue.time_slot =
                outletData.settings?.time_slot_setting === 'same_time_slot_for_all_days'
                    ? 'outlet_same_day'
                    : outletData.settings?.time_slot_setting === 'different_time_slots_for_different_days'
                      ? 'outlet_differant_day'
                      : 'outlet_same_day';

            if (outletData.settings?.time_slot_setting === 'same_time_slot_for_all_days') {
                console.log('Outlet schedules:', outletData.settings.schedules);
                let fieldData = [];
                if (outletData.settings.schedules && outletData.settings.schedules.length > 0) {
                    // For same time slot for all days, we expect one entry with all days
                    const scheduleData = outletData.settings.schedules[0];
                    if (scheduleData && scheduleData.time_slots) {
                        scheduleData.time_slots.forEach((item) => {
                            fieldData.push({
                                id: uuidv4(),
                                start_time: item.start_time,
                                end_time: item.end_time,
                            });
                        });
                    }
                }
                console.log('Outlet time fieldData:', fieldData);
                defaultValue.time = fieldData;
            }

            defaultValue.weak = {
                sunday: [],
                monday: [],
                tuesday: [],
                wednesday: [],
                thursday: [],
                friday: [],
                saturday: [],
            };

            if (outletData.settings?.time_slot_setting === 'different_time_slots_for_different_days') {
                console.log('Different days schedules:', outletData.settings.schedules);
                // Process the schedules by day
                if (outletData.settings.schedules && outletData.settings.schedules.length > 0) {
                    outletData.settings.schedules.forEach((schedule) => {
                        const days = schedule.days_of_week;
                        const timeSlots = schedule.time_slots || [];

                        days.forEach((day) => {
                            if (day === 1) {
                                timeSlots.forEach((item) =>
                                    defaultValue.weak.monday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                    })
                                );
                            }
                            if (day === 2) {
                                timeSlots.forEach((item) =>
                                    defaultValue.weak.tuesday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                    })
                                );
                            }
                            if (day === 3) {
                                timeSlots.forEach((item) =>
                                    defaultValue.weak.wednesday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                    })
                                );
                            }
                            if (day === 4) {
                                timeSlots.forEach((item) =>
                                    defaultValue.weak.thursday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                    })
                                );
                            }
                            if (day === 5) {
                                timeSlots.forEach((item) =>
                                    defaultValue.weak.friday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                    })
                                );
                            }
                            if (day === 6) {
                                timeSlots.forEach((item) =>
                                    defaultValue.weak.saturday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                    })
                                );
                            }
                            if (day === 7) {
                                timeSlots.forEach((item) =>
                                    defaultValue.weak.sunday.push({
                                        id: uuidv4(),
                                        start_time: item.start_time,
                                        end_time: item.end_time,
                                    })
                                );
                            }
                        });
                    });
                }
                console.log('Week time slots:', defaultValue.weak);
            }
            // Set plan_id if it exists in the outlet data
            if (outletData.plan_id) {
                defaultValue.plan_id = outletData.plan_id;
            }

            console.log('defaultValue', defaultValue);
            console.log('kitchen_time_weak before reset:', defaultValue.kitchen_time_weak);
            console.log('Key values check:', {
                convenience_charge: defaultValue.convenience_charge,
                iso_country_code: defaultValue.iso_country_code,
                kitchen_time_slot: defaultValue.kitchen_time_slot,
                rush_hour_setting: outletData.settings?.rush_hour_time_slot_setting,
            });

            reset({
                ...defaultValue,
                time: defaultValue.time ? defaultValue.time : [],
                weak: defaultValue.weak
                    ? defaultValue.weak
                    : {
                          sunday: [],
                          monday: [],
                          tuesday: [],
                          wednesday: [],
                          thursday: [],
                          friday: [],
                          saturday: [],
                      },
                kitchen_time: defaultValue.kitchen_time ? defaultValue.kitchen_time : [],
                kitchen_time_weak: defaultValue.kitchen_time_weak
                    ? defaultValue.kitchen_time_weak
                    : {
                          sunday: [],
                          monday: [],
                          tuesday: [],
                          wednesday: [],
                          thursday: [],
                          friday: [],
                          saturday: [],
                      },
            });
        }
    }, [editOutlet, subscriptionId, outletData]);

    const slub = [
        {
            value: '0',
            inputDisplay: '0%',
        },
        {
            value: '5',
            inputDisplay: '5%',
        },
        {
            value: '12',
            inputDisplay: '12%',
        },
        {
            value: '18',
            inputDisplay: '18%',
        },
        {
            value: '28',
            inputDisplay: '28%',
        },
    ];
    const gst_applicable = [
        {
            id: '0',
            label: 'No',
        },
        {
            id: '1',
            label: 'Yes',
        },
    ];
    const timeSlot = [
        {
            id: 'outlet_same_day',
            label: 'Available at same time for all days of the week',
        },
        {
            id: 'outlet_differant_day',
            label: 'Available at specific time for all days of the week',
        },
    ];

    const kitchen_timeSlot = [
        {
            id: '6',
            label: 'There is no rush hour',
        },
        {
            id: '7',
            label: 'Add some rush hour on all days',
        },
        {
            id: '8',
            label: 'Differant rush hour on differant days',
        },
    ];

    let countryCode = [];
    for (const [key, value] of Object.entries(allCountry)) {
        countryCode.push({
            value: key,
            inputDisplay: value,
        });
    }
    let currencyDataSymbol = [];
    let currencyDataCode = [];
    for (const [key1, value1] of Object.entries(currencyData)) {
        currencyDataSymbol.push({
            value: value1.symbol_native,
            label: `${value1.name} - ${value1.symbol} - ${value1.symbol_native} `,
        });
    }
    for (const [key1, value1] of Object.entries(currencyData)) {
        currencyDataCode.push({
            value: value1.code,
            label: value1.code,
        });
    }

    const storeDatatoLocalStorage = () => {
        const data = getValues();
        console.log(data);
        const getFormList = JSON.parse(localStorage.getItem('form_list'));
        if (editOutlet) {
            let index = getFormList.findIndex((item) => item.id === editOutlet);
            data.id = editOutlet;
            getFormList[index] = data;
            localStorage.setItem('form_list', JSON.stringify(getFormList));
        } else if (getFormList === null) {
            localStorage.setItem('form_list', JSON.stringify([]));
            const getFormData = JSON.parse(localStorage.getItem('form_list'));
            data.id = uuidv4();
            getFormData.push(data);
            localStorage.setItem('form_list', JSON.stringify(getFormData));
        } else {
            data.id = uuidv4();
            getFormList.push(data);
            localStorage.setItem('form_list', JSON.stringify(getFormList));
        }
    };

    const onSubmit = async (data) => {
        console.log(`afasfasdfas ==>`, data);
        const city = {};
        const state = {};

        const address_line_1 = {};
        const address_line_2 = {};
        const name = {};

        for (const property in data.translations.city) {
            city[parseInt(property)] = data.translations.city[property];
        }
        for (const property in data.translations.state) {
            state[parseInt(property)] = data.translations.state[property];
        }
        for (const property in data.translations.address_line_1) {
            address_line_1[parseInt(property)] = data.translations.address_line_1[property];
        }
        for (const property in data.translations.address_line_2) {
            address_line_2[parseInt(property)] = data.translations.address_line_2[property];
        }
        for (const property in data.translations.name) {
            name[parseInt(property)] = data.translations.name[property];
        }

        data.translations.city = city;
        data.translations.state = state;

        data.translations.address_line_1 = address_line_1;
        data.translations.address_line_2 = address_line_2;
        data.translations.name = name;

        // Build the outlet_data structure based on the API requirements
        const outlet_data = {
            translations: {
                name: data.translations.name,
            },
            contact_number: data.selectedCountryNumber
                ? `${data.selectedCountryNumber.dialCode}${data.selectedCountryNumber.number}`
                : data.contact_number || '918780049300',
            iso_country_code: data.country_code || 'IN',
            gst_applicable: data.gst_applicable === 0 ? 0 : 1,
            fssai_licence: data.fssai_licence || '10719019000301',
            latitude: data.latitude || '22.3039',
            longitude: data.longitude || '70.8022',
            service_radius: data.service_radius || '3',
            kitchen_preparation_time: data.kitchen_preparation_time || '40',
            delivery_charge_threshold: data.delivery_charge_threshold || '400',
            delivery_charge_above_threshold: data.delivery_charge_above_threshold || '300',
            delivery_charge_below_threshold: data.delivery_charge_below_threshold || '200',
            packaging_charge: data.packaging_charge || '40',
            convenience_charge: data.convenience_charge || '40',
            case_on_delivery: data.case_on_delivery || false,
            time_slot: data.time_slot || 'outlet_same_day',
            vegetarian_flag: data.vegetarian_flag ? 1 : 0,
            not_vegetarian_flag: data.non_vegetarian_flag ? 1 : 0,
            egg_flag: data.egg_flag ? 1 : 0,
            kitchen_time_slot: data.kitchen_time_slot || 6,
            gst_number: data.gst_applicable ? data.gst_number || '1er' : null,
            delivery_charges_gst_slab: data.gst_applicable ? data.delivery_charges_gst_slab || '0' : null,
            normal_delivery_radius: data.prize_per_kilometer ? data.normal_delivery_radius || '2' : '2',
            address: {
                country_code: data.selectedCountryNumber
                    ? `+${data.selectedCountryNumber.dialCode}`
                    : !isEmpty(editOutlet) && editOutlet.outlet_address.country_code
                      ? editOutlet.outlet_address.country_code
                      : '+91',
                contact_number: data.selectedCountryNumber
                    ? data.selectedCountryNumber.number
                    : !isEmpty(editOutlet) && editOutlet.outlet_address.contact_number
                      ? editOutlet.outlet_address.contact_number
                      : '8780049300',
                service_radius: data.service_radius || '3',
                longitude: data.longitude || '70.8022',
                latitude: data.latitude || '22.3039',
                iso_country_code: data.country_code || 'IN',
                normal_delivery_radius: data.prize_per_kilometer ? data.normal_delivery_radius || '2' : '2',
                translations: {
                    city: data.translations.city,
                    state: data.translations.state,
                    address_line_1: data.translations.address_line_1,
                    address_line_2: data.translations.address_line_2,
                },
            },
        };

        // Add settings to outlet_data
        outlet_data.settings = {
            use_central_payment_configurations: true,
            gst_applicable: data.gst_applicable === 0 ? 0 : 1,
            gst_number: data.gst_applicable ? data.gst_number || '1er' : null,
            delivery_charges_gst_slab: data.gst_applicable ? data.delivery_charges_gst_slab || '0' : null,
            kitchen_preparation_time: data.kitchen_preparation_time || '40',
            delivery_charge_threshold: data.delivery_charge_threshold || '400',
            delivery_charge_above_threshold: data.delivery_charge_above_threshold || '300',
            delivery_charge_below_threshold: data.delivery_charge_below_threshold || '200',
            extra_delivery_charge: data.prize_per_kilometer ? data.extra_delivery_charge || '30' : '30',
            packaging_charge: data.packaging_charge || '40',
            convenience_charge: data.convenience_charge || '40',
            vegetarian_flag: data.vegetarian_flag ? 1 : 0,
            not_vegetarian_flag: data.non_vegetarian_flag ? 1 : 0,
            egg_flag: data.egg_flag ? 1 : 0,
            currency_symbol: data.currency_symbol && data.currency_symbol[0] ? data.currency_symbol[0].value : '$',
            currency_code: data.currency_code && data.currency_code[0] ? data.currency_code[0].value : 'USD',
            delivery: data.delivery ? 1 : 0,
            dine_in: data.dine_in ? 1 : 0,
            takeaway: data.takeaway ? 1 : 0,
        };

        // Rush hour time slot
        let rush_hour_schedules;
        if (data.kitchen_time_slot === 6 || data.kitchen_time_slot === '6' || data.kitchen_time_slot === true) {
            outlet_data.settings.rush_hour_schedules = null;
            outlet_data.settings.rush_hour_time_slot_setting = 'no_time_slots';
        } else if (data.kitchen_time_slot === 7 || data.kitchen_time_slot === '7') {
            outlet_data.settings.rush_hour_time_slot_setting = 'same_time_slot_for_all_days';
            rush_hour_schedules = [];

            let singleTimeSlot =
                data.kitchen_time &&
                data.kitchen_time.length > 0 &&
                data.kitchen_time.filter((item, index) => {
                    if (
                        item.start_time !== undefined &&
                        item.end_time !== undefined &&
                        item.preparation_time_in_rush !== undefined
                    ) {
                        return item;
                    }
                });
            singleTimeSlot &&
                singleTimeSlot.length > 0 &&
                rush_hour_schedules.push({
                    days_of_week: [1, 2, 3, 4, 5, 6, 7],
                    time_slots: singleTimeSlot,
                });
            outlet_data.settings.rush_hour_schedules = rush_hour_schedules.length > 0 ? rush_hour_schedules : null;
            if (!outlet_data.settings.rush_hour_schedules) {
                outlet_data.settings.rush_hour_time_slot_setting = 'no_time_slots';
            }
        } else if (data.kitchen_time_slot === 8 || data.kitchen_time_slot === '8') {
            outlet_data.settings.rush_hour_time_slot_setting = 'different_time_slots_for_different_days';
            rush_hour_schedules = [];

            let sunday =
                data.kitchen_time_weak &&
                data.kitchen_time_weak.sunday &&
                data.kitchen_time_weak.sunday.filter((item, index) => {
                    if (
                        item.start_time !== undefined &&
                        item.end_time !== undefined &&
                        item.preparation_time_in_rush !== undefined
                    ) {
                        return item;
                    }
                });
            sunday &&
                sunday.length > 0 &&
                rush_hour_schedules.push({
                    days_of_week: [7],
                    time_slots: sunday,
                });

            let monday =
                data.kitchen_time_weak &&
                data.kitchen_time_weak.monday &&
                data.kitchen_time_weak.monday.filter((item, index) => {
                    if (
                        item.start_time !== undefined &&
                        item.end_time !== undefined &&
                        item.preparation_time_in_rush !== undefined
                    ) {
                        return item;
                    }
                });
            monday &&
                monday.length > 0 &&
                rush_hour_schedules.push({
                    days_of_week: [1],
                    time_slots: monday,
                });

            let tuesday =
                data.kitchen_time_weak &&
                data.kitchen_time_weak.tuesday &&
                data.kitchen_time_weak.tuesday.filter((item, index) => {
                    if (
                        item.start_time !== undefined &&
                        item.end_time !== undefined &&
                        item.preparation_time_in_rush !== undefined
                    ) {
                        return item;
                    }
                });
            tuesday &&
                tuesday.length > 0 &&
                rush_hour_schedules.push({
                    days_of_week: [2],
                    time_slots: tuesday,
                });

            let wednesday =
                data.kitchen_time_weak &&
                data.kitchen_time_weak.wednesday &&
                data.kitchen_time_weak.wednesday.filter((item, index) => {
                    if (
                        item.start_time !== undefined &&
                        item.end_time !== undefined &&
                        item.preparation_time_in_rush !== undefined
                    ) {
                        return item;
                    }
                });
            wednesday &&
                wednesday.length > 0 &&
                rush_hour_schedules.push({
                    days_of_week: [3],
                    time_slots: wednesday,
                });

            let thursday =
                data.kitchen_time_weak &&
                data.kitchen_time_weak.thursday &&
                data.kitchen_time_weak.thursday.filter((item, index) => {
                    if (
                        item.start_time !== undefined &&
                        item.end_time !== undefined &&
                        item.preparation_time_in_rush !== undefined
                    ) {
                        return item;
                    }
                });
            thursday &&
                thursday.length > 0 &&
                rush_hour_schedules.push({
                    days_of_week: [4],
                    time_slots: thursday,
                });

            let friday =
                data.kitchen_time_weak &&
                data.kitchen_time_weak.friday &&
                data.kitchen_time_weak.friday.filter((item, index) => {
                    if (
                        item.start_time !== undefined &&
                        item.end_time !== undefined &&
                        item.preparation_time_in_rush !== undefined
                    ) {
                        return item;
                    }
                });
            friday &&
                friday.length > 0 &&
                rush_hour_schedules.push({
                    days_of_week: [5],
                    time_slots: friday,
                });

            let saturday =
                data.kitchen_time_weak &&
                data.kitchen_time_weak.saturday &&
                data.kitchen_time_weak.saturday.filter((item, index) => {
                    if (
                        item.start_time !== undefined &&
                        item.end_time !== undefined &&
                        item.preparation_time_in_rush !== undefined
                    ) {
                        return item;
                    }
                });
            saturday &&
                saturday.length > 0 &&
                rush_hour_schedules.push({
                    days_of_week: [6],
                    time_slots: saturday,
                });

            outlet_data.settings.rush_hour_schedules = rush_hour_schedules.length > 0 ? rush_hour_schedules : null;
            if (!outlet_data.settings.rush_hour_schedules) {
                outlet_data.settings.rush_hour_time_slot_setting = 'no_time_slots';
            }
        } else {
            rush_hour_schedules = null;
            outlet_data.settings.rush_hour_time_slot_setting = 'no_time_slots';
            outlet_data.settings.rush_hour_schedules = null;
        }

        // Set outlet time
        let schedules;
        if (data.time_slot === 'outlet_same_day') {
            outlet_data.settings.time_slot_setting = 'same_time_slot_for_all_days';
            schedules = [];

            let singleTimeSlot =
                data.time &&
                data.time.length > 0 &&
                data.time.filter((item, index) => {
                    if (item.start_time !== undefined && item.end_time !== undefined) {
                        return item;
                    }
                });
            singleTimeSlot &&
                singleTimeSlot.length > 0 &&
                schedules.push({
                    days_of_week: [1, 2, 3, 4, 5, 6, 7],
                    time_slots: singleTimeSlot,
                });

            outlet_data.settings.schedules = schedules.length > 0 ? schedules : null;
            if (!outlet_data.settings.schedules) {
                setTimeSlotError(true);
                setLoader(false);
                return false;
            }
        } else if (data.time_slot === 'outlet_differant_day') {
            schedules = [];
            outlet_data.settings.time_slot_setting = 'different_time_slots_for_different_days';

            let sunday =
                data.weak &&
                data.weak.sunday &&
                data.weak.sunday.filter((item, index) => {
                    if (item.start_time !== undefined && item.end_time !== undefined) {
                        return item;
                    }
                });
            sunday &&
                sunday.length > 0 &&
                schedules.push({
                    days_of_week: [7],
                    time_slots: sunday,
                });
            let monday =
                data.weak &&
                data.weak.monday &&
                data.weak.monday.filter((item, index) => {
                    if (item.start_time !== undefined && item.end_time !== undefined) {
                        return item;
                    }
                });
            monday &&
                monday.length > 0 &&
                schedules.push({
                    days_of_week: [1],
                    time_slots: monday,
                });

            let tuesday =
                data.weak &&
                data.weak.tuesday &&
                data.weak.tuesday.filter((item, index) => {
                    if (item.start_time !== undefined && item.end_time !== undefined) {
                        return item;
                    }
                });
            tuesday &&
                tuesday.length > 0 &&
                schedules.push({
                    days_of_week: [2],
                    time_slots: tuesday,
                });

            let wednesday =
                data.weak &&
                data.weak.wednesday &&
                data.weak.wednesday.filter((item, index) => {
                    if (item.start_time !== undefined && item.end_time !== undefined) {
                        return item;
                    }
                });
            wednesday &&
                wednesday.length > 0 &&
                schedules.push({
                    days_of_week: [3],
                    time_slots: wednesday,
                });

            let thursday =
                data.weak &&
                data.weak.thursday &&
                data.weak.thursday.filter((item, index) => {
                    if (item.start_time !== undefined && item.end_time !== undefined) {
                        return item;
                    }
                });
            thursday &&
                thursday.length > 0 &&
                schedules.push({
                    days_of_week: [4],
                    time_slots: thursday,
                });

            let friday =
                data.weak &&
                data.weak.friday &&
                data.weak.friday.filter((item, index) => {
                    if (item.start_time !== undefined && item.end_time !== undefined) {
                        return item;
                    }
                });
            friday &&
                friday.length > 0 &&
                schedules.push({
                    days_of_week: [5],
                    time_slots: friday,
                });

            let saturday =
                data.weak &&
                data.weak.saturday &&
                data.weak.saturday.filter((item, index) => {
                    if (item.start_time !== undefined && item.end_time !== undefined) {
                        return item;
                    }
                });
            saturday &&
                saturday.length > 0 &&
                schedules.push({
                    days_of_week: [6],
                    time_slots: saturday,
                });

            outlet_data.settings.schedules = schedules.length > 0 ? schedules : null;
            if (!outlet_data.settings.schedules) {
                setTimeSlotError(true);
                setLoader(false);
                return false;
            }
        } else {
            outlet_data.settings.schedules = null;
            outlet_data.settings.time_slot_setting = 'no_time_slots';
        }
        setTimeSlotError(false);

        // Create the final request payload
        const requestPayload = {
            restaurant_id: restaurantId, // You can make this dynamic if needed
            outlet_data: outlet_data,
            legal_name: name[1], // You can make this dynamic if needed
        };

        // Add plan_id to the request payload if provided
        if (data.plan_id) {
            requestPayload.plan_id = data.plan_id[0]?.value;
        }
        setLoader(true);

        if (editOutlet && subscriptionId) {
            // Handle edit outlet case
            outlet_data.id = editOutlet.outlet_id;
            try {
                const response = await editOutletIntoRestaurant(subscriptionId, requestPayload);
                alert('Outlet edited successfully!');

                // Reset form to default values
                reset(defaultValue);

                // Clear any additional state
                setValue('selectedCountryNumber', null);

                // Dispatch editOutlet as false
                dispatch({
                    type: SET_EDIT_OUTLET,
                    payload: {
                        editOutlet: false,
                    },
                });

                // Redirect to list tab
                history.push('/subscription?tab=list_outlet');
            } catch (error) {
                console.error('Error editing outlet:', error);
                alert(error.message || 'Failed to edit outlet. Please try again.');
            }
        } else {
            try {
                // Make the API call to create outlet directly
                const response = await addOutletIntoRestaurant(requestPayload);
                alert('Outlet created successfully!');

                // Reset form to default values for creating another outlet
                reset(defaultValue);
                // Clear any additional state if needed
                setValue('selectedCountryNumber', null);

                // Redirect to list tab after successful creation
                history.push('/subscription?tab=list_outlet');
            } catch (error) {
                console.error('Error creating outlet:', error);
                alert(error.message || 'An error occurred. Please try again.');
            }
        }
    };
    return (
        <>
            <EuiForm component='form' onSubmit={handleSubmit(onSubmit)} style={{ marginTop: 20 }}>
                {availableLanguages &&
                    availableLanguages.map((item, index) => (
                        <TextField
                            key={item.language_id}
                            errors={errors}
                            isInvalid={
                                errors.translations &&
                                errors.translations.name &&
                                errors.translations.name.hasOwnProperty(item.language_id)
                            }
                            error={
                                errors.translations &&
                                errors.translations.name &&
                                errors.translations.name.hasOwnProperty(item.language_id) &&
                                errors.translations.name[item.language_id]
                                    ? errors.translations.name[item.language_id].message
                                    : ''
                            }
                            label={`${item.language_label} : Name`}
                            name={`translations.name.${item.language_id}`}
                            placeholder={`Enter the name of the outlet in ${item.language_label}`}
                            rules={{ required: 'Please enter name' }}
                            control={control}
                            watch={watch}
                        />
                    ))}
                {/* <NumberField
          label="Contact number"
          errors={errors}
          name="contact_number"
          placeholder="Enter the contact number of the outlet"
          rules={{
            required: "Please Enter your contact number",
            minLength: {
              value: 10,
              message: " length should be 10",
            },
            maxLength: {
              value: 10,
              message: " length should be 10",
            },
            validate: (value) => value >= 0 || "Please enter positive value",
          }}
          min={0}
          minlength={10}
          maxlength={10}
          control={control}
          watch={watch}
        /> */}

                <EuiFlexItem style={{ width: '100%' }}>
                    <MobileNumber
                        watch={watch}
                        setValue={setValue}
                        label={'Contact number'}
                        isInvalid={errors['contact_number']}
                        errors={errors}
                        placeholder='Enter the contact number of the outlet'
                        name={'contact_number'}
                        control={control}
                        rules={true}
                    />
                </EuiFlexItem>

                {availableLanguages &&
                    availableLanguages.map((item, index) => (
                        <Fragment key={item.language_id}>
                            <TextAreaField
                                errors={errors}
                                isInvalid={
                                    errors.translations &&
                                    errors.translations.address_line_1 &&
                                    errors.translations.address_line_1.hasOwnProperty(item.language_id)
                                }
                                error={
                                    errors.translations &&
                                    errors.translations.address_line_1 &&
                                    errors.translations.address_line_1.hasOwnProperty(item.language_id) &&
                                    errors.translations.address_line_1[item.language_id]
                                        ? errors.translations.address_line_1[item.language_id].message
                                        : ''
                                }
                                label={`${item.language_label} : Address Line 1`}
                                name={`translations.address_line_1.${item.language_id}`}
                                placeholder='Write address line 1'
                                rules={{ required: 'Please write address line 1' }}
                                control={control}
                                watch={watch}
                                rows={4}
                            />

                            <TextAreaField
                                errors={errors}
                                isInvalid={
                                    errors.translations &&
                                    errors.translations.address_line_2 &&
                                    errors.translations.address_line_2.hasOwnProperty(item.language_id)
                                }
                                error={
                                    errors.translations &&
                                    errors.translations.address_line_2 &&
                                    errors.translations.address_line_2.hasOwnProperty(item.language_id) &&
                                    errors.translations.address_line_2[item.language_id]
                                        ? errors.translations.address_line_2[item.language_id].message
                                        : ''
                                }
                                label={`${item.language_label} : Address Line 2`}
                                name={`translations.address_line_2.${item.language_id}`}
                                placeholder='Write address line 1'
                                rules={{ required: 'Please write address line 1' }}
                                control={control}
                                watch={watch}
                                rows={4}
                            />

                            <TextField
                                errors={errors}
                                isInvalid={
                                    errors.translations &&
                                    errors.translations.city &&
                                    errors.translations.city.hasOwnProperty(item.language_id)
                                }
                                error={
                                    errors.translations &&
                                    errors.translations.city &&
                                    errors.translations.city.hasOwnProperty(item.language_id) &&
                                    errors.translations.city[item.language_id]
                                        ? errors.translations.city[item.language_id].message
                                        : ''
                                }
                                label={`${item.language_label} : City`}
                                name={`translations.city.${item.language_id}`}
                                placeholder='Please Enter city'
                                rules={{ required: 'Please enter city' }}
                                control={control}
                                watch={watch}
                            />

                            <TextField
                                errors={errors}
                                isInvalid={
                                    errors.translations &&
                                    errors.translations.state &&
                                    errors.translations.state.hasOwnProperty(item.language_id)
                                }
                                error={
                                    errors.translations &&
                                    errors.translations.state &&
                                    errors.translations.state.hasOwnProperty(item.language_id) &&
                                    errors.translations.state[item.language_id]
                                        ? errors.translations.state[item.language_id].message
                                        : ''
                                }
                                label={`${item.language_label} : State`}
                                name={`translations.state.${item.language_id}`}
                                placeholder='Please Enter state'
                                rules={{ required: 'Please enter state' }}
                                control={control}
                                watch={watch}
                            />
                        </Fragment>
                    ))}
                <SelectField
                    errors={errors}
                    label='Country'
                    name='iso_country_code'
                    options={countryCode}
                    valueOfSelected={watch('iso_country_code') ? watch('iso_country_code') : 'IN'}
                    onChange={(value) => setValue('iso_country_code', value)}
                    placeholder='Please enter your country'
                    // rules={{ required: "Please Enter your country" }}
                    control={control}
                    watch={watch}
                    defaultValue={watch('iso_country_code') ? watch('iso_country_code') : 'IN'}
                />

                <EuiFlexGroup alignItems='center' style={{ marginTop: '10px' }}>
                    <EuiFlexItem grow={false}>
                        <EuiFormRow isInvalid={errors.gst_applicable} fullWidth={true}>
                            <Controller
                                render={({ onChange }) => (
                                    <EuiRadioGroup
                                        options={gst_applicable}
                                        idSelected={
                                            watch('gst_applicable') !== undefined
                                                ? String(watch('gst_applicable'))
                                                : '0'
                                        }
                                        onChange={(id) =>
                                            setValue('gst_applicable', id === '0' ? 0 : id === '1' ? 1 : 0)
                                        }
                                        legend={{
                                            children: <span> Do you want to apply GST or not ? </span>,
                                        }}
                                    />
                                )}
                                name='gst_applicable'
                                defaultValue={watch('gst_applicable') ? watch('gst_applicable') : 0}
                                control={control}
                                fullWidth={true}
                            />
                        </EuiFormRow>
                    </EuiFlexItem>
                </EuiFlexGroup>
                {watch('gst_applicable') === 1 && (
                    <>
                        <TextField
                            errors={errors}
                            label='Gst number'
                            name='gst_number'
                            placeholder='Please enter gst number'
                            rules={{
                                required: 'Please enter gst number',
                                pattern: {
                                    value: /^[A-Za-z0-9]+$/,
                                    message: 'GST number should contain only letters and numbers',
                                },
                            }}
                            control={control}
                            watch={watch}
                        />
                        <SelectField
                            errors={errors}
                            label='Delivery charge GST Slug'
                            name='delivery_charges_gst_slab'
                            options={slub}
                            valueOfSelected={
                                watch('delivery_charges_gst_slab') ? watch('delivery_charges_gst_slab') : slub[0].value
                            }
                            onChange={(value) => setValue('delivery_charges_gst_slab', value)}
                            placeholder='Please select gst slub'
                            rules={{
                                required: 'Please enter delivery charge gst slub',
                            }}
                            control={control}
                            watch={watch}
                            defaultValue={
                                watch('delivery_charges_gst_slab') ? watch('delivery_charges_gst_slab') : slub[0].value
                            }
                        />
                    </>
                )}

                <TextField
                    errors={errors}
                    label='FSSAI Licence'
                    name='fssai_licence'
                    placeholder='Please enter fssai licence'
                    rules={{
                        pattern: {
                            value: /^[A-Za-z0-9]+$/,
                            message: 'FSSAI licence should contain only letters and numbers',
                        },
                    }}
                    control={control}
                    watch={watch}
                />

                <NumberField
                    label='Latitude'
                    errors={errors}
                    name='latitude'
                    placeholder='Please Enter latitude'
                    rules={{
                        required: 'Please enter latitude',
                    }}
                    step='any'
                    control={control}
                />

                <NumberField
                    label='Longitude'
                    errors={errors}
                    name='longitude'
                    placeholder='Please Enter longitude'
                    rules={{
                        required: 'Please enter longitude',
                    }}
                    step='any'
                    control={control}
                />
                <NumberField
                    label='Radius'
                    errors={errors}
                    name='service_radius'
                    placeholder='Please Enter radius'
                    rules={{
                        required: 'Please enter radius',
                        validate: (value) => value >= 0 || 'Radius cannot be negative',
                    }}
                    prepend={
                        <EuiToolTip content='content'>
                            <EuiText size='s'>In km</EuiText>
                        </EuiToolTip>
                    }
                    step='any'
                    min={0}
                    control={control}
                />

                <NumberField
                    label='Kitchen Prep Time Slots'
                    errors={errors}
                    name='kitchen_preparation_time'
                    placeholder='Please enter kitchen preparation time'
                    rules={{
                        required: 'Please enter kitchen preparation time',
                        validate: (value) => value > 0 || 'Please enter positive value',
                    }}
                    step='any'
                    min={0}
                    control={control}
                />

                <RadioField
                    name='kitchen_time_slot'
                    label='Decide kitchen rush hour slot'
                    options={kitchen_timeSlot}
                    idSelected={String(
                        watch('kitchen_time_slot') === 6
                            ? '6'
                            : watch('kitchen_time_slot') === 7
                              ? '7'
                              : watch('kitchen_time_slot') === 8
                                ? '8'
                                : '6'
                    )}
                    onChange={(id) =>
                        setValue('kitchen_time_slot', id === '6' ? 6 : id === '7' ? 7 : id === '8' ? 8 : 6)
                    }
                    control={control}
                    defaultValue={6}
                />

                <EuiFlexGroup style={{ width: '80%' }}>
                    <EuiFlexItem>
                        {(watch('kitchen_time_slot') === 7 || watch('kitchen_time_slot') === '7') && (
                            <>
                                <EuiFlexGroup
                                    style={{
                                        background: '#9895CA',
                                        marginTop: '20px',
                                        marginBottom: '30px',
                                    }}
                                >
                                    <EuiFlexItem grow={2}>Days</EuiFlexItem>
                                    <EuiFlexItem grow={4}>Start Time</EuiFlexItem>
                                    <EuiFlexItem grow={4}>End Time</EuiFlexItem>
                                    <EuiFlexItem grow={4}>Kitchen Prepation Time</EuiFlexItem>
                                </EuiFlexGroup>

                                <KitchenTimeSlot
                                    control={control}
                                    setValue={setValue}
                                    name='kitchen_time'
                                    errors={errors}
                                    watch={watch}
                                />
                            </>
                        )}
                        {(watch('kitchen_time_slot') === 8 || watch('kitchen_time_slot') === '8') && (
                            <>
                                <EuiFlexGroup
                                    style={{
                                        background: '#9895CA',
                                        marginTop: '20px',
                                        marginBottom: '30px',
                                    }}
                                >
                                    <EuiFlexItem grow={2}>Days</EuiFlexItem>
                                    <EuiFlexItem grow={4}>Start Time</EuiFlexItem>
                                    <EuiFlexItem grow={4}>End Time</EuiFlexItem>
                                    <EuiFlexItem grow={4}>Kitchen Prepation Time</EuiFlexItem>
                                    <EuiFlexItem grow={1}></EuiFlexItem>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                            }}
                                        >
                                            <strong> Sunday </strong>{' '}
                                        </EuiFlexItem>

                                        <KitchenTimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='kitchen_time_weak.sunday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Monday </strong>
                                        </EuiFlexItem>
                                        <KitchenTimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='kitchen_time_weak.monday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            {' '}
                                            <strong> Tuesday </strong>{' '}
                                        </EuiFlexItem>

                                        <KitchenTimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='kitchen_time_weak.tuesday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            {' '}
                                            <strong> Wednesday </strong>{' '}
                                        </EuiFlexItem>

                                        <KitchenTimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='kitchen_time_weak.wednesday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Thursday </strong>
                                        </EuiFlexItem>
                                        <KitchenTimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='kitchen_time_weak.thursday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Friday </strong>
                                        </EuiFlexItem>
                                        <KitchenTimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='kitchen_time_weak.friday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Saturday </strong>
                                        </EuiFlexItem>
                                        <KitchenTimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='kitchen_time_weak.saturday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>
                            </>
                        )}
                    </EuiFlexItem>
                </EuiFlexGroup>

                <NumberField
                    label='Delivery charge thresold'
                    errors={errors}
                    name='delivery_charge_threshold'
                    placeholder='Please enter delivery charge thresold'
                    rules={{
                        required: 'Please enter delivery charge thresold',
                        validate: (value) => value >= 0 || 'Delivery charge threshold cannot be negative',
                    }}
                    step='any'
                    min={0}
                    control={control}
                />

                <NumberField
                    label='Above thresold'
                    errors={errors}
                    name='delivery_charge_above_threshold'
                    placeholder='Please enter delivery above thresold'
                    rules={{
                        required: 'Please enter delivery above thresold',
                        validate: (value) => value >= 0 || 'Delivery charge above threshold cannot be negative',
                    }}
                    step='any'
                    min={0}
                    control={control}
                />

                <NumberField
                    label='Below thresold'
                    errors={errors}
                    name='delivery_charge_below_threshold'
                    placeholder='Please enter delivery below thresold'
                    rules={{
                        required: 'Please enter delivery below thresold',
                        validate: (value) => value >= 0 || 'Delivery charge below threshold cannot be negative',
                    }}
                    step='any'
                    min={0}
                    control={control}
                />

                <EuiFormRow>
                    <EuiFlexGroup alignItems='center'>
                        <EuiFlexItem grow={false}>
                            <Controller
                                render={({ field: { onChange, value } }) => (
                                    <EuiCheckbox
                                        id='prize_per_kilometer'
                                        onChange={(e) => {
                                            onChange(e.target.checked);
                                            setValue('prize_per_kilometer', e.target.checked);
                                        }}
                                        checked={value}
                                        compressed
                                    />
                                )}
                                name='prize_per_kilometer'
                                control={control}
                                defaultValue={false}
                            />
                        </EuiFlexItem>
                        <EuiFlexItem grow={false}>
                            <EuiText>Charge additional prize per kilometer ?</EuiText>
                        </EuiFlexItem>
                    </EuiFlexGroup>
                </EuiFormRow>

                {watch('prize_per_kilometer') && (
                    <>
                        <NumberField
                            label='Normal delivery radius'
                            errors={errors}
                            name='normal_delivery_radius'
                            placeholder='Please enter normal delivery radius'
                            rules={{
                                required: 'Please enter normal delivery radius',
                                validate: (value) => value > 0 || 'Please enter positive value',
                            }}
                            prepend={
                                <EuiToolTip content='content'>
                                    <EuiText size='s'>In km</EuiText>
                                </EuiToolTip>
                            }
                            step='any'
                            min={0}
                            control={control}
                        />

                        <NumberField
                            label='Extra Charge'
                            errors={errors}
                            name='extra_delivery_charge'
                            placeholder='Please enter extra charge'
                            rules={{
                                required: 'Please enter normal delivery radius',
                                validate: (value) => value >= 0 || 'Please enter positive value',
                            }}
                            step='any'
                            min={0}
                            control={control}
                        />
                    </>
                )}

                <NumberField
                    label='Packaging charge per order'
                    errors={errors}
                    name='packaging_charge'
                    placeholder='Please enter packaging charge'
                    rules={{
                        required: 'Please enter packaging charge',
                        validate: (value) => value >= 0 || 'Packaging charge cannot be negative',
                    }}
                    step='any'
                    min={0}
                    control={control}
                />

                <NumberField
                    label='Convenience Charge'
                    errors={errors}
                    name='convenience_charge'
                    placeholder='Please enter convenience charge'
                    rules={{
                        required: 'Please enter convenience charge',
                        validate: (value) => value >= 0 || 'Convenience charge cannot be negative',
                    }}
                    step='any'
                    min={0}
                    control={control}
                />

                <EuiFormRow fullWidth={true}>
                    <>
                        <EuiFlexGroup alignItems='center'>
                            <EuiFlexItem grow={false}>
                                <CheckBoxField
                                    name='vegetarian_flag'
                                    defaultValue={false}
                                    control={control}
                                    setValue={setValue}
                                />
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                                <EuiText>Vegetarian</EuiText>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                        <EuiFlexGroup alignItems='center'>
                            <EuiFlexItem grow={false}>
                                <CheckBoxField
                                    name='non_vegetarian_flag'
                                    defaultValue={false}
                                    control={control}
                                    setValue={setValue}
                                />
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                                <EuiText>Non-vegetarian</EuiText>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                        <EuiFlexGroup alignItems='center'>
                            <EuiFlexItem grow={false}>
                                <CheckBoxField
                                    name='egg_flag'
                                    defaultValue={false}
                                    control={control}
                                    setValue={setValue}
                                />
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                                <EuiText>Egg</EuiText>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </>
                </EuiFormRow>

                <ComboBoxField
                    options={currencyDataSymbol}
                    selectedOptions={watch('currency_symbol') ? watch('currency_symbol') : []}
                    onChange={(value) => setValue('currency_symbol', value)}
                    map={true}
                    errors={errors}
                    label='Currency Symbol'
                    name='currency_symbol'
                    placeholder='Please enter your currency symbol'
                    rules={{
                        validate: (value) => (value && value.length > 0) || 'Please select currency symbol',
                    }}
                    control={control}
                    singleSelection={{ asPlainText: true }}
                />

                <ComboBoxField
                    options={subscriptionConfigurations}
                    selectedOptions={(() => {
                        const planId = watch('plan_id');
                        if (!planId || subscriptionConfigurations.length === 0) return [];
                        const selectedConfig = subscriptionConfigurations.find((config) => config.value === planId);
                        return selectedConfig ? [selectedConfig] : [];
                    })()}
                    onChange={(value) => {
                        // Store only the plan ID (value) in the form
                        setValue('plan_id', value && value.length > 0 ? value[0].value : null);
                    }}
                    errors={errors}
                    label='Subscription Plan'
                    name='plan_id'
                    placeholder='Please select a subscription plan'
                    rules={{
                        validate: (value) => value || 'Please select a subscription plan',
                    }}
                    control={control}
                    singleSelection={{ asPlainText: true }}
                />

                <ComboBoxField
                    options={currencyDataCode}
                    selectedOptions={watch('currency_code') ? watch('currency_code') : []}
                    onChange={(value) => {
                        setValue('currency_code', value);
                        delete errors['currency_code'];
                    }}
                    map={true}
                    errors={errors}
                    label='Currency Code'
                    name='currency_code'
                    placeholder='Please enter your currency code'
                    rules={{
                        validate: (value) => (value && value.length > 0) || 'Please select currency code',
                    }}
                    control={control}
                    singleSelection={{ asPlainText: true }}
                />

                <EuiFormRow>
                    <>
                        <EuiFlexGroup alignItems='center'>
                            <EuiFlexItem grow={false}>
                                <CheckBoxField
                                    name='case_on_delivery'
                                    defaultValue={false}
                                    control={control}
                                    setValue={setValue}
                                />
                            </EuiFlexItem>
                            <EuiFlexItem grow={false}>
                                <EuiText>Case on delivery</EuiText>
                            </EuiFlexItem>
                        </EuiFlexGroup>
                    </>
                </EuiFormRow>
                <EuiFlexGroup alignItems='center' style={{ marginTop: '10px' }}>
                    <EuiFlexItem grow={false}>
                        <EuiFormRow isInvalid={errors.time_slot} fullWidth={true}>
                            <Controller
                                render={({ field: { onChange } }) => (
                                    <EuiRadioGroup
                                        options={timeSlot}
                                        idSelected={
                                            watch('time_slot') === 'outlet_differant_day'
                                                ? 'outlet_differant_day'
                                                : 'outlet_same_day'
                                        }
                                        onChange={(id) =>
                                            setValue(
                                                'time_slot',
                                                id === 'outlet_differant_day'
                                                    ? 'outlet_differant_day'
                                                    : 'outlet_same_day'
                                            )
                                        }
                                        legend={{
                                            children: <span> Decide Outlet Timing </span>,
                                        }}
                                    />
                                )}
                                name='time_slot'
                                defaultValue={
                                    watch('time_slot') === 'outlet_differant_day'
                                        ? 'outlet_differant_day'
                                        : 'outlet_same_day'
                                }
                                control={control}
                                fullWidth={true}
                            />
                        </EuiFormRow>
                    </EuiFlexItem>
                </EuiFlexGroup>
                <EuiFlexGroup style={{ width: '80%' }}>
                    <EuiFlexItem>
                        {(watch('time_slot') === 'outlet_same_day' || watch('time_slot') === undefined) && (
                            <>
                                <EuiFlexGroup
                                    style={{
                                        background: '#9895CA',
                                        marginTop: '20px',
                                        marginBottom: '30px',
                                    }}
                                >
                                    <EuiFlexItem grow={2}>Days</EuiFlexItem>
                                    <EuiFlexItem grow={4}>Start Time</EuiFlexItem>
                                    <EuiFlexItem grow={4}>End Time</EuiFlexItem>
                                </EuiFlexGroup>

                                <TimeSlot control={control} setValue={setValue} name='time' watch={watch} />
                            </>
                        )}
                        {watch('time_slot') === 'outlet_differant_day' && (
                            <>
                                <EuiFlexGroup
                                    style={{
                                        background: '#9895CA',
                                        marginTop: '20px',
                                        marginBottom: '30px',
                                    }}
                                >
                                    <EuiFlexItem grow={2}>Days</EuiFlexItem>
                                    <EuiFlexItem grow={4}>Start Time</EuiFlexItem>
                                    <EuiFlexItem grow={4}>End Time</EuiFlexItem>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Sunday </strong>{' '}
                                        </EuiFlexItem>
                                        <TimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='weak.sunday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Monday </strong>
                                        </EuiFlexItem>
                                        <TimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='weak.monday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            {' '}
                                            <strong> Tuesday </strong>{' '}
                                        </EuiFlexItem>

                                        <TimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='weak.tuesday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            {' '}
                                            <strong> Wednesday </strong>{' '}
                                        </EuiFlexItem>

                                        <TimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='weak.wednesday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Thursday </strong>
                                        </EuiFlexItem>
                                        <TimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='weak.thursday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Friday </strong>
                                        </EuiFlexItem>
                                        <TimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='weak.friday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>

                                <EuiFlexGroup
                                    style={{
                                        marginBottom: 30,
                                        paddingBottom: 40,
                                        borderBottom: '1px solid black',
                                    }}
                                >
                                    <EuiFlexGroup
                                        style={{
                                            flexDirection: 'column',
                                            textAlign: 'center',
                                        }}
                                    >
                                        <EuiFlexItem
                                            style={{
                                                textAlign: 'left',
                                                marginLeft: '3%',
                                            }}
                                        >
                                            <strong> Saturday </strong>
                                        </EuiFlexItem>

                                        <TimeSlot
                                            control={control}
                                            setValue={setValue}
                                            name='weak.saturday'
                                            watch={watch}
                                        />
                                    </EuiFlexGroup>
                                </EuiFlexGroup>
                            </>
                        )}
                    </EuiFlexItem>
                </EuiFlexGroup>
                {timeSlotError && <EuiText style={{ color: 'red' }}> Please select time slot </EuiText>}

                <EuiFlexGroup style={{ marginTop: 10 }}>
                    {/* <EuiFlexItem grow={false} onClick={storeDatatoLocalStorage}>
                        <EuiButton fill>Save local draft</EuiButton>
                    </EuiFlexItem> */}

                    <EuiFlexItem grow={false}>
                        <EuiButton isLoading={loader} type='submit' fill>
                            {subscriptionId ? 'Edit into database' : 'Save into database'}
                        </EuiButton>
                    </EuiFlexItem>
                </EuiFlexGroup>
            </EuiForm>
        </>
    );
};

export default CreateOutletScreen;
